import { GisPolygon } from '@app/components/openstreetmap/interfaces/gis-component.interfaces';
import { ExternalSceneRowViewModel } from './external-scenes.models';

/** Prefix of the map polygon ids drawn for scene footprints, so they can be removed without touching other polygons. */
export const FOOTPRINT_POLYGON_ID_PREFIX = 'external-scene:';

const FOOTPRINT_STROKE_COLOR = '#2563eb';
const FOOTPRINT_FILL_OPACITY = 0.06;
const FOOTPRINT_STROKE_WIDTH = 2;
const MIN_RING_POINTS = 3;

/** A ring in Leaflet order: `[latitude, longitude]`. */
type LatLngRing = [number, number][];

interface LatLngLike {
  lat: number;
  lng: number;
}

/** What the map's `drawingCompleted` event carries for a finished shape (structurally typed, no Leaflet import). */
export interface DrawnShapeLike {
  coordinates: [number, number][];
  layer?: unknown;
}

/**
 * Subscribes to the Leaflet layer's one-shot `remove` event so the caller learns when the user deletes the shape.
 * Returns false (and subscribes to nothing) when the shape has no layer that supports `once`, i.e. it cannot be
 * tracked and must not be treated as still drawn.
 */
export function watchDrawnShapeRemoval(shape: DrawnShapeLike, onRemoved: () => void): boolean {
  const layer = shape.layer as { once?: (type: string, handler: () => void) => unknown } | null | undefined;
  if (typeof layer?.once !== 'function') {
    return false;
  }
  layer.once('remove', onRemoved);
  return true;
}

function isFinitePair(value: unknown): value is [number, number] {
  return Array.isArray(value) && value.length >= 2 && Number.isFinite(value[0]) && Number.isFinite(value[1]);
}

/** GeoJSON positions `[lon, lat]` to a Leaflet ring `[lat, lon]`; drops the closing duplicate point. */
function toLeafletRing(positions: unknown): LatLngRing {
  if (!Array.isArray(positions)) {
    return [];
  }
  const ring: LatLngRing = positions.filter(isFinitePair).map(([lon, lat]) => [lat, lon]);
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (ring.length > 1 && first[0] === last[0] && first[1] === last[1]) {
    ring.pop();
  }
  return ring;
}

/**
 * Outer rings of a footprint geometry in Leaflet order. Understands `Polygon` and `MultiPolygon`; anything else
 * (null, malformed JSON, other types) yields no rings, so a bad footprint never breaks the page.
 */
export function footprintRings(footprintGeoJson: string | null): LatLngRing[] {
  if (!footprintGeoJson) {
    return [];
  }
  let geometry: { type?: string; coordinates?: unknown };
  try {
    geometry = JSON.parse(footprintGeoJson);
  } catch {
    return [];
  }
  const coordinates = geometry?.coordinates;
  if (!Array.isArray(coordinates)) {
    return [];
  }
  const outerRings: unknown[] =
    geometry.type === 'Polygon'
      ? [coordinates[0]]
      : geometry.type === 'MultiPolygon'
        ? coordinates.map(polygon => (Array.isArray(polygon) ? polygon[0] : null))
        : [];
  return outerRings.map(toLeafletRing).filter(ring => ring.length >= MIN_RING_POINTS);
}

/** Map polygons for the scene footprints of the given rows (one per outer ring). */
export function buildFootprintPolygons(
  rows: readonly Pick<ExternalSceneRowViewModel, 'id' | 'name' | 'footprintGeoJson'>[]
): GisPolygon[] {
  return rows.flatMap(row =>
    footprintRings(row.footprintGeoJson).map((ring, index) => ({
      id: `${FOOTPRINT_POLYGON_ID_PREFIX}${row.id}#${index}`,
      name: row.name,
      coordinates: ring,
      tooltip: row.name,
      strokeColor: FOOTPRINT_STROKE_COLOR,
      strokeWidth: FOOTPRINT_STROKE_WIDTH,
      fillColor: FOOTPRINT_STROKE_COLOR,
      fillOpacity: FOOTPRINT_FILL_OPACITY,
      interactive: false
    }))
  );
}

function isLatLngLike(value: unknown): value is LatLngLike {
  return !!value && Number.isFinite((value as LatLngLike).lat) && Number.isFinite((value as LatLngLike).lng);
}

/** The shape's current vertices: from the Leaflet layer when it exposes `getLatLngs()` (it follows edits), else null. */
function ringFromLayer(layer: unknown): LatLngRing | null {
  const getLatLngs = (layer as { getLatLngs?: () => unknown } | null)?.getLatLngs;
  if (typeof getLatLngs !== 'function') {
    return null;
  }
  const latLngs = getLatLngs.call(layer);
  const first = Array.isArray(latLngs) ? latLngs[0] : null;
  const points = Array.isArray(first) ? first : latLngs;
  if (!Array.isArray(points)) {
    return null;
  }
  const ring = points.filter(isLatLngLike).map((point): [number, number] => [point.lat, point.lng]);
  return ring.length >= MIN_RING_POINTS ? ring : null;
}

/** A rectangle is reported as two opposite corners; a ring needs all four. */
function ringFromCoordinates(coordinates: [number, number][]): LatLngRing {
  const points = coordinates.filter(isFinitePair);
  if (points.length === 2) {
    const [[latA, lonA], [latB, lonB]] = points;
    return [
      [latA, lonA],
      [latA, lonB],
      [latB, lonB],
      [latB, lonA]
    ];
  }
  return points;
}

/**
 * GeoJSON `Polygon` string of a shape drawn on the map, or `null` when it has fewer than three vertices.
 * Leaflet order `[lat, lon]` is converted to GeoJSON `[lon, lat]` and the ring is closed.
 */
export function drawnShapeToGeoJson(shape: DrawnShapeLike | null): string | null {
  if (!shape) {
    return null;
  }
  const ring = ringFromLayer(shape.layer) ?? ringFromCoordinates(shape.coordinates);
  if (ring.length < MIN_RING_POINTS) {
    return null;
  }
  const positions = ring.map(([lat, lon]) => [lon, lat]);
  positions.push(positions[0]);
  return JSON.stringify({ type: 'Polygon', coordinates: [positions] });
}

/** `lat, lon` with four decimals; empty when either is unknown. */
export function formatCoordinates(latitude: number | null, longitude: number | null): string {
  if (latitude === null || longitude === null || !Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return '';
  }
  return `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
}
